export type InsightsLoadState = { status: 'loading' } | { status: 'ready'; failedSections?: readonly string[] }

export type InsightsMoney = {
  locale: string
  currencyCode: string
  currencyName: string
}

export type InsightImage = {
  name: string
  imageUrl: string
}

export type InsightTestVerdict = 'win' | 'similar'

export type InsightTestResult = {
  title: string
  date: string
  verdict: InsightTestVerdict
  summary: string
  covers: string
  area: 'description' | 'price' | 'photo' | 'offer'
  applyLabel?: string
}

export type CustomerGroupMix = readonly [number, number, number, number, number]

export type InsightProduct = InsightImage & { id: string }

export type ProductPair = {
  productIds: readonly [string, string]
  orders: number
  lift: number
}

export type CustomerLifecycle =
  | { grouped: 0 }
  | { grouped: number; neverBought: number; inSmallGroups: number; groups: CustomerGroupMix }

export type ShopperCluster = {
  name: string
  size: number
  standoutHeadline?: string
  image: InsightImage & { contents?: readonly InsightImage[] }
  signals: readonly string[]
  why: string
  idea: string
}

export type InsightsData = {
  money: InsightsMoney
  updatedLabel: string
  shoppers: number
  withoutOrders: number
  oneTimeBuyers: number
  repeatBuyers: number
  lifecycle: CustomerLifecycle
  spendPrediction: { classified: number; high: number; medium: number; low: number }
  typicalSpend: number
  spendBands: readonly { label: string; customers: number }[]
  repeatHabits: { differentKinds: number; oneKind: number; sameProduct: number }
  productTypes: readonly { name: string; customers: number }[]
  customerTags: readonly { tag: string; customers: number }[]
  shopperClusters: readonly ShopperCluster[]
}

export const CUSTOMER_GROUP_NAMES = [
  'Best customers',
  'Steady shoppers',
  'Just arrived',
  'Drifting away',
  'Gone quiet',
] as const

export function createFormatters(money: InsightsMoney) {
  const count = new Intl.NumberFormat(money.locale)
  const currency = new Intl.NumberFormat(money.locale, { style: 'currency', currency: money.currencyCode })
  const currencyWhole = new Intl.NumberFormat(money.locale, {
    style: 'currency',
    currency: money.currencyCode,
    maximumFractionDigits: 0,
  })
  return {
    count: (x: number) => count.format(x),
    money: (x: number) => currency.format(x),
    moneyWhole: (x: number) => currencyWhole.format(Math.round(x)),
  }
}

export type InsightsFormatters = ReturnType<typeof createFormatters>

export function percentOf(x: number, base: number): string {
  if (!base) return '0%'
  const p = (x / base) * 100
  if (p > 0 && p < 0.5) return '<1%'
  return `${Math.round(p)}%`
}

export function plural(format: InsightsFormatters, x: number, one: string, many: string): string {
  return `${format.count(x)} ${x === 1 ? one : many}`
}

export type CoverageLevel = 'none' | 'thin' | 'partial' | 'full'

export function coverageLevel(covered: number, eligible: number): CoverageLevel {
  if (!covered) return 'none'
  if (covered < 50 || covered / eligible < 0.2) return 'thin'
  if (covered / eligible < 0.8) return 'partial'
  return 'full'
}

export const PAIR_MIN_LIFT_FOR_STRONG = 2
export const PAIR_MIN_ORDERS_FOR_STRONG = 10

export function isStrongPair(pair: ProductPair): boolean {
  return pair.lift >= PAIR_MIN_LIFT_FOR_STRONG && pair.orders >= PAIR_MIN_ORDERS_FOR_STRONG
}

export function findProduct(products: readonly InsightProduct[], id: string): InsightProduct | undefined {
  return products.find((p) => p.id === id)
}

export function isSectionFailed(loadState: InsightsLoadState, key: string): boolean {
  return loadState.status === 'ready' && (loadState.failedSections?.includes(key) ?? false)
}

export type CustomerLifecycleGroups = Extract<CustomerLifecycle, { groups: CustomerGroupMix }>

export function lifecycleGroupsOf(data: InsightsData): CustomerLifecycleGroups | null {
  const lifecycle = data.lifecycle
  if (!('groups' in lifecycle) || coverageLevel(lifecycle.grouped, data.shoppers) === 'none') return null
  return lifecycle
}

export function buyersOf(data: InsightsData): number {
  return data.oneTimeBuyers + data.repeatBuyers
}
